/**
 * GraphQL mutations for Audit primitive
 *
 * 8 mutations covering:
 * - CRUD operations (create, update, delete)
 * - Retention management (extend retention period)
 * - Report generation (compliance reports)
 * - Template integration (FERP Metadata DSL v1.0)
 * - Execution through runtime engines
 * - Change tracking for external monitoring
 *
 * FERP Critical: Immutability enforcement for life-critical audits
 */

import { gql } from '@apollo/client';
import { AUDIT_FIELDS, AUDIT_REPORT_FIELDS } from './fragments';

/**
 * Create a new audit record
 * Generates backend Snowflake ID
 */
export const CREATE_AUDIT = gql`
  ${AUDIT_FIELDS}
  mutation CreateAudit($input: CreateAuditInput!) {
    createAudit(input: $input) {
      ...AuditFields
    }
  }
`;

/**
 * Update an existing audit record
 * Limited fields only - audit reports are mostly immutable
 * Immutability enforced for life-critical audits
 */
export const UPDATE_AUDIT = gql`
  ${AUDIT_FIELDS}
  mutation UpdateAudit($tenantId: String!, $id: ID!, $input: UpdateAuditInput!) {
    updateAudit(tenantId: $tenantId, id: $id, input: $input) {
      ...AuditFields
    }
  }
`;

/**
 * Delete an audit record
 * Soft delete - respects retention period (HIPAA 6y, SOX 7y, FDA lifetime)
 */
export const DELETE_AUDIT = gql`
  mutation DeleteAudit($tenantId: String!, $id: ID!) {
    deleteAudit(tenantId: $tenantId, id: $id)
  }
`;

/**
 * Extend retention period for an audit
 * Required for regulatory compliance when audit needs longer retention
 * Tracks authorization and reason for audit trail
 */
export const EXTEND_AUDIT_RETENTION = gql`
  ${AUDIT_FIELDS}
  mutation ExtendAuditRetention($tenantId: String!, $id: ID!, $input: ExtendRetentionInput!) {
    extendAuditRetention(tenantId: $tenantId, id: $id, input: $input) {
      ...AuditFields
    }
  }
`;

/**
 * Generate compliance report for an audit
 * Supports multiple formats: PDF, CSV, JSON, HTML
 * Can include evidence and validation details
 */
export const GENERATE_AUDIT_REPORT = gql`
  ${AUDIT_REPORT_FIELDS}
  mutation GenerateAuditReport($tenantId: String!, $id: ID!, $input: GenerateReportInput!) {
    generateAuditReport(tenantId: $tenantId, id: $id, input: $input) {
      ...AuditReportFields
    }
  }
`;

/**
 * Create audit from Metadata DSL template
 * FERP integration point 1: from_template()
 * Supports template versioning and override values
 */
export const CREATE_AUDIT_FROM_TEMPLATE = gql`
  ${AUDIT_FIELDS}
  mutation CreateAuditFromTemplate($input: CreateFromTemplateInput!) {
    createAuditFromTemplate(input: $input) {
      ...AuditFields
    }
  }
`;

/**
 * Execute audit through runtime engine
 * FERP integration point 2: execute()
 * Routes to StateMachine, Saga, Actor, or DAG engine
 */
export const EXECUTE_AUDIT = gql`
  ${AUDIT_FIELDS}
  mutation ExecuteAudit($tenantId: String!, $id: ID!, $input: ExecuteAuditInput!) {
    executeAudit(tenantId: $tenantId, id: $id, input: $input) {
      ...AuditFields
    }
  }
`;

/**
 * Track configuration change for external monitoring
 * FERP integration point 6: track_change()
 * Returns event ID for correlation across monitoring systems
 */
export const TRACK_AUDIT_CHANGE = gql`
  mutation TrackAuditChange($tenantId: String!, $id: ID!, $input: TrackChangeInput!) {
    trackAuditChange(tenantId: $tenantId, id: $id, input: $input)
  }
`;
